import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useState } from "react";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FlashContext from "components/FlashMessages/FlashContext";
import FormFooter from "components/FormDefault/Footer";
import PageLoading from "components/loading/PageLoading";
import WarningBar from "components/warning/WarningBar";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageAuditTrail } from "hooks/useAnalytics/pages/auditTrail";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { showEditDrawer } from "./EditDrawer";
import { DELETE_AUDIT_TRAIL_WEBHOOK, GET_AUDIT_TRAIL_WEBHOOK } from "./gql";
import styles from "./styles.module.css";
import View from "./View";

const AuditTrailConfig = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const [isAskedToDelete, askToDelete] = useState(false);

  const { loading, data, error } = useQuery(GET_AUDIT_TRAIL_WEBHOOK);

  const [deleteWebhook, { loading: isDeleting }] = useMutation(DELETE_AUDIT_TRAIL_WEBHOOK, {
    refetchQueries: [{ query: GET_AUDIT_TRAIL_WEBHOOK }],
  });

  useTitle(`Audit trail · Configuration · ${accountName}`);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageAuditTrail.Configuration,
  });

  const integration = data?.auditTrailWebhook;

  const onEditMode = () => {
    trackSegmentAnalyticsEvent("Edit clicked");
    showEditDrawer({
      integration: integration || undefined,
    });
  };

  const onRemove = useCallback(async () => {
    if (!isAskedToDelete) {
      return askToDelete(true);
    }

    if (!isDeleting && isAskedToDelete) {
      try {
        await deleteWebhook();

        askToDelete(false);
        trackSegmentAnalyticsEvent("Delete clicked");
        reportSuccess({ message: "Audit trail integration successfully disabled" });
      } catch (e) {
        onError(e);
      }
    }
  }, [
    isAskedToDelete,
    isDeleting,
    deleteWebhook,
    reportSuccess,
    trackSegmentAnalyticsEvent,
    onError,
  ]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLoading />;
  }

  const isViewMode = !!integration;

  const openCreateForm = () => {
    trackSegmentAnalyticsEvent("Set up clicked");
    showEditDrawer();
  };

  return (
    <>
      <PageInfoHeader title="Configuration" />

      <div className={styles.auditTrailWrapper}>
        <div className={styles.integrationHeaderWrapper}>
          <h3 className={styles.integrationHeaderTitle}>Audit trail configuration</h3>

          {isViewMode && integration && (
            <Box gap="large">
              <Button type="button" onPress={onEditMode} variant="primary">
                Edit
              </Button>

              <Button type="button" variant="dangerPrimary" loading={isDeleting} onPress={onRemove}>
                {isAskedToDelete ? "Confirm" : "Delete"}
              </Button>
            </Box>
          )}
        </div>

        {isAskedToDelete && (
          <WarningBar>
            <p>
              Note that by disabling the Audit trail you will stop receiving audit event webhooks.
            </p>
          </WarningBar>
        )}

        {integration && <View {...integration} />}

        {!integration && (
          <FormFooter top>
            <Button type="button" variant="primary" onPress={openCreateForm}>
              Set up
            </Button>
          </FormFooter>
        )}
      </div>
    </>
  );
};

export default AuditTrailConfig;
